import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";

const AUTH_BG = "https://images.unsplash.com/photo-1764512680324-048f158cab2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBqZXdlbHJ5JTIwc3RvcmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODg4NjMyNjd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  // First-run Super Admin setup
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [setupEmail, setSetupEmail] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    api.get("/auth/setup-status")
      .then(({ data }) => {
        if (data.needs_setup) {
          setSetupNeeded(true);
          setSetupEmail(data.email || "");
          setEmail(data.email || "");
        }
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.role_name === "Customer" || (u.customer_id && (u.permissions || []).includes("portal:view"))) {
        navigate("/portal");
      } else {
        navigate("/");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/setup-password", { password });
      toast.success("Password set. Please sign in.");
      setSetupNeeded(false);
      setConfirm("");
      setPassword("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={AUTH_BG} alt="OM GOLD showroom" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-[#1A1A1A]/20 to-transparent" />
        <div className="absolute bottom-10 left-10 text-white">
          <div className="font-display text-7xl font-bold tracking-wide text-[#E8Cf9c]">OM GOLD</div>
          <div className="text-sm tracking-[0.3em] uppercase mt-2 text-white/80">Jewellery Enterprise Suite</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#FDFBF7]">
        <div className="w-full max-w-md fade-in">
          <div className="lg:hidden font-display text-6xl font-bold tracking-wide text-[#C5A059] mb-2">OM GOLD</div>

          {setupNeeded ? (
            <>
              <div className="flex items-center gap-2 text-[#C5A059] mb-2">
                <ShieldCheck size={22} weight="fill" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold">First-time setup</span>
              </div>
              <h1 className="font-display text-3xl font-semibold text-[#1A1A1A]">Create your password</h1>
              <p className="text-sm text-[#8A8A8A] mt-1 mb-8">
                Set the Super Admin password for <span className="font-semibold text-[#1A1A1A]">{setupEmail}</span> to finish setup.
              </p>

              <form onSubmit={submitSetup} className="space-y-4" data-testid="setup-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">New Password</Label>
                  <div className="relative mt-1.5">
                    <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                      placeholder="At least 8 characters" className="bg-white border-[#E6E2D6] pr-10" data-testid="setup-password" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" data-testid="setup-toggle-password">
                      {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">Confirm Password</Label>
                  <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                    placeholder="Re-enter password" className="mt-1.5 bg-white border-[#E6E2D6]" data-testid="setup-confirm" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#C5A059] hover:bg-[#B38F4E] text-white h-11" data-testid="setup-submit">
                  {loading ? "Saving…" : "Set Password & Continue"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold text-[#1A1A1A]">Sign in</h1>
              <p className="text-sm text-[#8A8A8A] mt-1 mb-8">Enter your credentials to access the ERP.</p>

              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">Email</Label>
                  <Input type="text" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@om-gold.in or username" className="mt-1.5 bg-white border-[#E6E2D6]" data-testid="login-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">Password</Label>
                  <div className="relative mt-1.5">
                    <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                      placeholder="••••••••" className="bg-white border-[#E6E2D6] pr-10" data-testid="login-password" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" data-testid="toggle-password">
                      {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#C5A059] hover:bg-[#B38F4E] text-white h-11" data-testid="login-submit">
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </>
          )}

          <div className="mt-10 flex items-center justify-center gap-2 text-[11px] text-[#8A8A8A]">
            <img src="/microcore-logo.png" alt="MICROCORE" className="h-6 w-6 object-contain" />
            Developed by <a href="https://www.microcoreitsolutions.com/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] font-semibold hover:underline" data-testid="microcore-link-login">MICROCORE IT SOLUTIONS</a>
          </div>
        </div>
      </div>
    </div>
  );
}
